import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Home, 
  Calendar, 
  User, 
  LogIn, 
  UserPlus,
  Menu,
  X,
  LogOut,
  BarChart3,
  Briefcase,
  Trophy,
  Users
} from "lucide-react";
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { resolveProfileImageUrl } from "@/utils/profileImageUrl";

export default function Navigation() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [signedLogoUrl, setSignedLogoUrl] = useState<string | null>(null);
  const [signedProfileImageUrl, setSignedProfileImageUrl] = useState<string | null>(null);
  const [showHighlights, setShowHighlights] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  
  console.log('Navigation - isAuthenticated:', isAuthenticated);
  console.log('Navigation - user:', user);

  const fetchLogo = async () => {
    try {
      console.log('Navigation: Fetching logo data...');
      const response = await fetch('/api/admin/content/public');
      console.log('Navigation: Logo fetch response status:', response.status);
      
      const data = await response.json();
      console.log('Navigation: Logo fetch data:', data);
      console.log('Navigation: API data.data object:', data.data);
      
      if (data.success && data.data.logoUrl) {
        setSignedLogoUrl(data.data.logoUrl);
      } else {
        setSignedLogoUrl(null);
      }
    } catch (error) {
      console.error('Navigation: Error fetching logo:', error);
      setSignedLogoUrl(null);
    }
  };

  useEffect(() => {
    fetchLogo();

    // Listen for content updates
    const handleContentUpdate = () => {
      console.log('Navigation: Content update event received, refetching logo');
      fetchLogo();
    };
    
    window.addEventListener('contentUpdated', handleContentUpdate);
    return () => window.removeEventListener('contentUpdated', handleContentUpdate);
  }, []);

  // Also refetch logo when user changes to ensure all users see the logo
  useEffect(() => {
    fetchLogo(); // Fetch logo for all users, not just admins
  }, [user]);

  // Check highlights navigation settings
  const checkHighlightsNavigation = async () => {
    console.log('Navigation: checkHighlightsNavigation function called');
    try {
      const response = await fetch('/api/highlights');
      if (response.ok) {
        const data = await response.json();
        console.log('Navigation: Highlights data:', data);
        if (data.success && data.data.showInMainNav) {
          console.log('Navigation: Setting showHighlights to true');
          setShowHighlights(true);
        } else {
          console.log('Navigation: Setting showHighlights to false');
          setShowHighlights(false);
        }
      }
    } catch (error) {
      console.error('Error checking highlights navigation:', error);
    }
  };

  useEffect(() => {
    console.log('Navigation: useEffect triggered, calling checkHighlightsNavigation');
    checkHighlightsNavigation();
  }, []);

  // Resolve profile image URL (LinkedIn/CDN direct; S3/local signed)
  const loadProfileImageUrl = async (imageUrl: string) => resolveProfileImageUrl(imageUrl);

  // Update signed profile image URL when user changes
  useEffect(() => {
    console.log('Navigation: Profile image useEffect triggered. User:', user);
    console.log('Navigation: Profile image URL:', user?.profileImageUrl);
    
    if (user?.profileImageUrl) {
      console.log('Navigation: Calling getSignedProfileImageUrl with:', user.profileImageUrl);
      loadProfileImageUrl(user.profileImageUrl).then(url => {
        console.log('Navigation: Signed URL returned:', url);
        setSignedProfileImageUrl(url);
      });
    } else {
      console.log('Navigation: No profile image URL, setting signed URL to null');
      setSignedProfileImageUrl(null);
    }
  }, [user?.profileImageUrl]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getDashboardLink = () => {
    if (!user) return '/';
    switch (user.role) {
      case 'admin':
        return '/admin/dashboard';
      case 'mentor':
        return '/mentor/dashboard';
      case 'aspirant':
        return '/aspirant/dashboard';
      default:
        return '/';
    }
  };

  const getProfileLink = () => {
    if (!user) return '/';
    switch (user.role) {
      case 'admin':
        return '/admin/profile';
      case 'mentor':
        return '/mentor/profile';
      case 'aspirant':
        return '/aspirant/profile';
      default:
        return '/';
    }
  };

  return (
    <nav className="bg-secondary text-white shadow-xl border-b border-secondary-light">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center group py-2"
          >
            {signedLogoUrl ? (
              <img 
                src={signedLogoUrl} 
                alt="Udyoga Pramoda Logo" 
                className="h-16 w-auto rounded-lg object-contain bg-surface shadow-lg group-hover:shadow-xl transition-all duration-300" 
                onLoad={() => console.log('Navigation: Logo loaded successfully:', signedLogoUrl)}
                onError={(e) => {
                  console.log('Navigation: Logo failed to load:', signedLogoUrl);
                  console.log('Navigation: Image error:', e);
                  setSignedLogoUrl(null); // Fallback to UP logo if image fails
                }}
                style={{ maxHeight: '64px' }}
              />
            ) : (
              <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center group-hover:bg-primary-dark transition-all duration-300 shadow-lg group-hover:shadow-xl">
                <span className="text-2xl font-bold text-white">UP</span>
              </div>
            )}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            <Link 
              to="/" 
              className="flex items-center space-x-2 px-4 py-2 rounded-lg text-white hover:bg-secondary-light hover:text-accent transition-all duration-300 font-medium"
            >
              <Home className="w-4 h-4" />
              <span>Home</span>
            </Link>
            
            <Link 
              to="/events" 
              className="flex items-center space-x-2 px-4 py-2 rounded-lg text-white hover:bg-secondary-light hover:text-accent transition-all duration-300 font-medium"
            >
              <Calendar className="w-4 h-4" />
              <span>Events</span>
            </Link>
            
            <Link 
              to="/jobs" 
              className="flex items-center space-x-2 px-4 py-2 rounded-lg text-white hover:bg-secondary-light hover:text-accent transition-all duration-300 font-medium"
            >
              <Briefcase className="w-4 h-4" />
              <span>Jobs</span>
            </Link>
            
            {showHighlights && (
              <Link 
                to="/highlights" 
                className="flex items-center space-x-2 px-4 py-2 rounded-lg text-white hover:bg-secondary-light hover:text-accent transition-all duration-300 font-medium"
              >
                <Trophy className="w-4 h-4" />
                <span>Highlights</span>
              </Link>
            )}
            
            {/* Show Dashboard and Profile only when authenticated */}
            {isAuthenticated && user && (
              <>
                <Link 
                  to={getDashboardLink()} 
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg text-white hover:bg-secondary-light hover:text-accent transition-all duration-300 font-medium"
                >
                  <BarChart3 className="w-4 h-4" />
                  <span>Dashboard</span>
                </Link>
                
                <Link 
                  to={getProfileLink()} 
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg text-white hover:bg-secondary-light hover:text-accent transition-all duration-300 font-medium"
                >
                  <User className="w-4 h-4" />
                  <span>Profile</span>
                </Link>
              </>
            )}
          </div>

          {/* Desktop Auth Buttons */}
          <div className="hidden md:flex items-center space-x-3">
            {isAuthenticated && user ? (
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2 text-white">
                  <div className="w-8 h-8 bg-secondary-light rounded-full flex items-center justify-center border border-border">
                    {signedProfileImageUrl ? (
                      <img 
                        key={signedProfileImageUrl}
                        src={signedProfileImageUrl} 
                        alt="Profile" 
                        className="w-8 h-8 rounded-full object-cover"
                        onError={() => {
                          if (user?.profileImageUrl && signedProfileImageUrl !== user.profileImageUrl) {
                            setSignedProfileImageUrl(user.profileImageUrl);
                          }
                        }}
                      />
                    ) : (
                      <User className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <span className="text-sm font-medium text-white">
                    {user?.firstName ? `${user.firstName} ${user.lastName || ''}` : user?.email}
                  </span>
                </div>
                <Button 
                  variant="ghost" 
                  onClick={handleLogout}
                  className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-secondary hover:border-white transition-all duration-300 font-medium px-4 py-2"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            ) : (
              <>
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-secondary hover:border-white transition-all duration-300 font-medium px-6 py-2"
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    className="bg-primary hover:bg-primary-dark text-white font-medium px-6 py-2 transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Sign Up
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="md:hidden p-2 rounded-lg text-white hover:bg-secondary-light hover:text-accent transition-colors"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-secondary-light bg-secondary">
            <div className="px-4 py-6 space-y-4">
              {/* Mobile Nav Links */}
              <div className="space-y-2">
                <Link 
                  to="/" 
                  className="flex items-center space-x-3 px-4 py-3 rounded-lg text-white hover:bg-secondary-light hover:text-accent transition-colors font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Home className="w-5 h-5" />
                  <span>Home</span>
                </Link>
                
                <Link 
                  to="/events" 
                  className="flex items-center space-x-3 px-4 py-3 rounded-lg text-white hover:bg-secondary-light hover:text-accent transition-colors font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Calendar className="w-5 h-5" />
                  <span>Events</span>
                </Link>
                
                <Link 
                  to="/jobs" 
                  className="flex items-center space-x-3 px-4 py-3 rounded-lg text-white hover:bg-secondary-light hover:text-accent transition-colors font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Briefcase className="w-5 h-5" />
                  <span>Jobs</span>
                </Link>
                
                {showHighlights && (
                  <Link 
                    to="/highlights" 
                    className="flex items-center space-x-3 px-4 py-3 rounded-lg text-white hover:bg-secondary-light hover:text-accent transition-colors font-medium"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Trophy className="w-5 h-5" />
                    <span>Highlights</span>
                  </Link>
                )}
                
                {/* Show Dashboard and Profile only when authenticated */}
                {isAuthenticated && user && (
                  <>
                    <Link 
                      to={getDashboardLink()} 
                      className="flex items-center space-x-3 px-4 py-3 rounded-lg text-white hover:bg-secondary-light hover:text-accent transition-colors font-medium"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <BarChart3 className="w-5 h-5" />
                      <span>Dashboard</span>
                    </Link>
                    
                    <Link 
                      to={getProfileLink()} 
                      className="flex items-center space-x-3 px-4 py-3 rounded-lg text-white hover:bg-secondary-light hover:text-accent transition-colors font-medium"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <User className="w-5 h-5" />
                      <span>Profile</span>
                    </Link>
                  </>
                )}
              </div>

              {/* Mobile Auth Buttons */}
              <div className="pt-4 border-t border-secondary-light space-y-3">
                {isAuthenticated && user ? (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3 px-4 py-3 text-white">
                      <div className="w-10 h-10 bg-secondary-light rounded-full flex items-center justify-center border border-border">
                        {signedProfileImageUrl ? (
                          <img 
                            key={signedProfileImageUrl}
                            src={signedProfileImageUrl} 
                            alt="Profile" 
                            className="w-10 h-10 rounded-full object-cover"
                            onError={() => {
                              if (user?.profileImageUrl && signedProfileImageUrl !== user.profileImageUrl) {
                                setSignedProfileImageUrl(user.profileImageUrl);
                              }
                            }}
                          />
                        ) : (
                          <User className="w-5 h-5 text-white" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-white">
                          {user?.firstName ? `${user.firstName} ${user.lastName || ''}` : user?.email}
                        </p>
                        <p className="text-sm text-gray-300 capitalize">{user?.role}</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        handleLogout();
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full bg-transparent border-2 border-white text-white hover:bg-white hover:text-secondary hover:border-white transition-all duration-300 font-medium py-3"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </Button>
                  </div>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}>
                      <Button 
                        variant="outline" 
                        className="w-full bg-transparent border-2 border-white text-white hover:bg-white hover:text-secondary hover:border-white transition-all duration-300 font-medium py-3"
                      >
                        <LogIn className="w-4 h-4 mr-2" />
                        Sign In
                      </Button>
                    </Link>
                    <Link to="/register" onClick={() => setIsMobileMenuOpen(false)}>
                      <Button 
                        className="w-full bg-primary hover:bg-primary-dark text-white font-medium py-3 transition-all duration-300"
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Sign Up
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
} 